/**
 * What the author may put in each binding slot, and how a choice is named
 * (CT-5641 · fix/widgets).
 *
 * The Data step produces three ordered pools — the measures, the groupers and
 * the exposed columns — and every slot of the chosen type draws from exactly
 * one of them, decided by its `x-field-kinds`:
 *
 * | Role | Pool | Example |
 * |---|---|---|
 * | `AGGREGATE` | measures | `KPI.value`, `COST_KPI.budget` |
 * | `DIMENSION` | groupers | `BAR_CHART.x`, `SPEND_BREAKDOWN.sub_category` |
 * | `COLUMN` | exposed columns | `BOARD.lane`, `ACTIVITY.occurred_at` |
 *
 * A slot that declares two (`CORRELATION.x` is `COLUMN|AGGREGATE`) draws from
 * both, and in practice only one of the pools has anything in it: a summary
 * exposes no columns and a record set has no measures.
 *
 * ## Why a ref rather than the item itself
 *
 * The form stores one string per slot, as it always has, and the payload
 * builder resolves it against the pools it builds anyway. A **column** is its
 * `fieldUuid` — unchanged, so a widget saved before this still reopens — and a
 * **measure** or **grouper** is its form row id, which is what survives
 * reordering. The implicit row count has no row, so it travels under the alias
 * it is sent as.
 */

import { WIDGET_FIELD_ROLES } from '../constants/widgetContract.js';
import { dataTypeFits } from './widgetDataTypes.js';

const EMPTY = Object.freeze([]);

/**
 * The ref of the measure the builder sends when the author chose none: a
 * summary with no measure counts rows, and `record_count` is the alias that
 * count travels under (see `AGGREGATE_ALIAS`).
 */
export const ROW_COUNT_REF = 'record_count';

/** A measure row's ref. Row ids are minted by the form, hydrated rows too. */
export const measureRef = (measure) => measure?.id || ROW_COUNT_REF;

/** A grouper row's ref, falling back to the alias its position gives it. */
export const grouperRef = (grouper, index) => grouper?.id || `dim_${index + 1}`;

/** A column's ref is its field uuid — what every other selection stores. */
export const columnRef = (fieldUuid) => fieldUuid ?? '';

export const POOL_KIND = Object.freeze({
  MEASURE: 'measure',
  DIMENSION: 'dimension',
  COLUMN: 'column',
});

/** Which pools a slot draws from, in the order they are offered. */
export function poolKindsForSlot(slot) {
  const kinds = [];
  if (slot?.roles?.includes(WIDGET_FIELD_ROLES.AGGREGATE)) kinds.push(POOL_KIND.MEASURE);
  if (slot?.roles?.includes(WIDGET_FIELD_ROLES.DIMENSION)) kinds.push(POOL_KIND.DIMENSION);
  if (slot?.roles?.includes(WIDGET_FIELD_ROLES.COLUMN)) kinds.push(POOL_KIND.COLUMN);
  return kinds;
}

/**
 * Does this pool item fit this slot?
 *
 * Both halves of the type's rule, the same two the measure picker applies: the
 * operation has to be one the slot accepts (`x-aggregates`), and what reaches
 * the slot has to be a type it takes. A pool item's `dataType` is already what
 * it *produces* — the caller resolves a measure's through `aggregateOutputType`
 * — because that is what the backend checks.
 */
export function itemFitsSlot(item, slot) {
  if (!item || !slot) return false;
  if (item.kind === POOL_KIND.MEASURE) {
    if (!slot.roles?.includes(WIDGET_FIELD_ROLES.AGGREGATE)) return false;
    if (slot.aggregates?.length && !slot.aggregates.includes(item.aggregate)) return false;
  }
  return dataTypeFits(item.dataType, slot.dataTypes ?? EMPTY);
}

/**
 * The pool items this slot may be given, in pool order.
 *
 * @param {object} slot From `readSlotModel`.
 * @param {{ measures?: Array, dimensions?: Array, columns?: Array }} pools
 *   Each item `{ ref, label, dataType, aggregate?, kind }`.
 */
export function slotCandidates(slot, pools = {}) {
  const byKind = {
    [POOL_KIND.MEASURE]: pools.measures ?? EMPTY,
    [POOL_KIND.DIMENSION]: pools.dimensions ?? EMPTY,
    [POOL_KIND.COLUMN]: pools.columns ?? EMPTY,
  };
  return poolKindsForSlot(slot)
    .flatMap((kind) => byKind[kind])
    .filter((item) => itemFitsSlot(item, slot));
}

/** Has the author answered this slot? An array slot needs at least one. */
export const slotIsAnswered = (assignments, slot) => {
  const value = assignments?.[slot.key];
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

/**
 * The required slots that cannot be filled at all: nothing in the Data step
 * fits them and the author has not answered them, so the contract will come
 * out empty whatever they do on this tab.
 *
 * This is the case the builder used to lose silently — a `COST_KPI` whose
 * `amount` wants `DECIMAL|NUMERIC|MONEY` and a Data step that counts rows,
 * which produces an `INTEGER`. The save went through and `publish` answered
 * `DSW012 {missing: bindings}` with nothing to say which slot was meant.
 *
 * @param {Array} slots From `readSlotModel().slots`, record-key slots dropped.
 * @param {object} assignments `{ [slotKey]: ref | ref[] }`.
 * @param {object} pools See `slotCandidates`.
 */
export function unfillableSlots(slots = EMPTY, assignments = {}, pools = {}) {
  return slots.filter((slot) => slot.required
    && !slotIsAnswered(assignments, slot)
    && slotCandidates(slot, pools).length === 0);
}

export default slotCandidates;
